import { useEffect, useState } from "react";
import { fetchOverview } from "@/lib/api";
import Seo from "@/components/Seo";
import { downloadCsv, today } from "@/lib/csv";
import TrustHistoryChart from "@/components/TrustHistoryChart";
import CategoryHeatmap from "@/components/CategoryHeatmap";
import { Download } from "lucide-react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Legend,
    Cell,
} from "recharts";

export default function Statistics() {
    const [overview, setOverview] = useState([]);

    useEffect(() => {
        fetchOverview().then(setOverview);
    }, []);

    const chartHeight = 480;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="statistics-page">
            <Seo
                title="Statistik og kategori-heatmap"
                description="Dyk ned i statistikken — kategori-heatmap over partiernes præstationer på klima, udlændinge, økonomi, velfærd, sundhed, ældre, arbejdsmarked og forsvar. CSV-eksport."
                keywords="politisk statistik, heatmap, kategori-analyse, klima, udlændinge, økonomi, velfærd, sundhed, rangliste, Folketinget"
                path="/statistik"
            />
            <div className="mb-12">
                <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                    Sammenligning
                </span>
                <h1 className="font-serif-dm text-5xl text-royal mt-2">
                    Den store tillidsgraf
                </h1>
                <p className="text-[#4F5A7A] mt-3 max-w-2xl">
                    Se hvordan partierne klarer sig på holdte løfter, brudte løfter,
                    dokumenterede løgne og valgflæsk — side om side.
                </p>
            </div>

            {/* HISTORISK TILLIDSGRAF */}
            <div className="mb-8">
                <TrustHistoryChart defaultDays={30} metric="trust_score" />
            </div>

            {/* HISTORISK LØGNE */}
            <div className="mb-8">
                <TrustHistoryChart
                    defaultDays={30}
                    metric="lies_count"
                    title="Historisk løgne-graf"
                />
            </div>

            {/* HISTORISK VALGFLÆSK */}
            <div className="mb-8">
                <TrustHistoryChart
                    defaultDays={30}
                    metric="valgflaesk_count"
                    title="Historisk valgflæsk-graf"
                />
            </div>

            {/* KATEGORI-HEATMAP */}
            <div className="mb-8">
                <CategoryHeatmap />
            </div>

            {/* TRUST SCORE */}
            <ChartSection
                title="Tillidsscore"
                subtitle="Andelen af løfter, der er holdt (fuldt + delvist) af det totale antal."
            >
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart data={overview} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                        <CartesianGrid stroke="#EEF0F7" vertical={false} />
                        <XAxis dataKey="short_name" tick={{ fill: "#4F5A7A", fontWeight: 600 }} />
                        <YAxis domain={[0, 100]} unit="%" tick={{ fill: "#4F5A7A" }} />
                        <Tooltip
                            contentStyle={tooltipStyle}
                            formatter={(v) => [`${v}%`, "Tillidsscore"]}
                            labelFormatter={(l, p) => p?.[0]?.payload?.party_name || l}
                        />
                        <Bar dataKey="trust_score" radius={[8, 8, 0, 0]}>
                            {overview.map((d) => (
                                <Cell key={d.party_id} fill={d.color} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </ChartSection>

            {/* PROMISE BREAKDOWN */}
            <ChartSection
                title="Løftefordeling pr. parti"
                subtitle="Samlet oversigt over holdte, brudte, delvise og afventende løfter."
            >
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart data={overview} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                        <CartesianGrid stroke="#EEF0F7" vertical={false} />
                        <XAxis dataKey="short_name" tick={{ fill: "#4F5A7A", fontWeight: 600 }} />
                        <YAxis tick={{ fill: "#4F5A7A" }} />
                        <Tooltip
                            contentStyle={tooltipStyle}
                            labelFormatter={(l, p) => p?.[0]?.payload?.party_name || l}
                        />
                        <Legend />
                        <Bar dataKey="holdt" stackId="a" name="Holdt" fill="#186A3B" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="delvist" stackId="a" name="Delvist" fill="#C78300" />
                        <Bar dataKey="afventer" stackId="a" name="Afventer" fill="#4F5A7A" />
                        <Bar dataKey="brudt" stackId="a" name="Brudt" fill="#BB1B2D" radius={[8, 8, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </ChartSection>

            {/* LIES & VALGFLÆSK */}
            <ChartSection
                title="Dokumenterede løgne & valgflæsk"
                subtitle="Optælling af politiske løgne og tom valgretorik baseret på mediearkivet."
            >
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart data={overview} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                        <CartesianGrid stroke="#EEF0F7" vertical={false} />
                        <XAxis dataKey="short_name" tick={{ fill: "#4F5A7A", fontWeight: 600 }} />
                        <YAxis tick={{ fill: "#4F5A7A" }} />
                        <Tooltip
                            contentStyle={tooltipStyle}
                            labelFormatter={(l, p) => p?.[0]?.payload?.party_name || l}
                        />
                        <Legend />
                        <Bar dataKey="lies_count" name="Løgne" fill="#BB1B2D" radius={[6, 6, 0, 0]} />
                        <Bar dataKey="valgflaesk_count" name="Valgflæsk" fill="#C78300" radius={[6, 6, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </ChartSection>

            {/* TABLE */}
            <div className="bg-white border border-[#E5E8F0] rounded-2xl p-6 md:p-8 mt-8">
                <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                    <h3 className="font-serif-dm text-2xl text-royal">Rangliste</h3>
                    <button
                        onClick={() => {
                            const rows = [
                                ["#", "Parti", "Kortnavn", "Tillidsscore", "Løfter i alt", "Holdt", "Delvist", "Brudt", "Afventer", "Løgne", "Valgflæsk", "Dage uden brud"],
                                ...overview.map((p, i) => [
                                    i + 1,
                                    p.party_name,
                                    p.short_name,
                                    `${p.trust_score} %`,
                                    p.promises_total,
                                    p.holdt,
                                    p.delvist,
                                    p.brudt,
                                    p.afventer,
                                    p.lies_count,
                                    p.valgflaesk_count,
                                    p.days_since_last_break ?? "—",
                                ]),
                            ];
                            downloadCsv(rows, `loftebarometer-rangliste-${today()}.csv`);
                        }}
                        data-testid="stats-csv-download"
                        title="Download rangliste som CSV"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-white border border-[#E5E8F0] text-royal hover:bg-royal-soft transition-colors"
                    >
                        <Download className="w-3.5 h-3.5" /> CSV
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="stats-table">
                        <thead>
                            <tr className="text-left text-[#4F5A7A] border-b border-[#E5E8F0]">
                                <th className="py-3">#</th>
                                <th className="py-3">Parti</th>
                                <th className="py-3 text-right">Tillidsscore</th>
                                <th className="py-3 text-right">Holdt</th>
                                <th className="py-3 text-right">Brudt</th>
                                <th className="py-3 text-right">Løgne</th>
                                <th className="py-3 text-right">Valgflæsk</th>
                                <th className="py-3 text-right">Dage uden brud</th>
                            </tr>
                        </thead>
                        <tbody>
                            {overview.map((p, i) => (
                                <tr
                                    key={p.party_id}
                                    className="border-b border-[#EEF0F7] last:border-0 hover:bg-[#F7F8FB]"
                                >
                                    <td className="py-3 font-serif-dm text-xl text-royal">{i + 1}</td>
                                    <td className="py-3">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-white"
                                                style={{ backgroundColor: p.color }}
                                            >
                                                {p.short_name}
                                            </span>
                                            <span className="font-medium">{p.party_name}</span>
                                        </div>
                                    </td>
                                    <td className="py-3 text-right font-semibold text-royal">
                                        {p.trust_score}%
                                    </td>
                                    <td className="py-3 text-right text-[#186A3B]">{p.holdt}</td>
                                    <td className="py-3 text-right text-[#BB1B2D]">{p.brudt}</td>
                                    <td className="py-3 text-right">{p.lies_count}</td>
                                    <td className="py-3 text-right">{p.valgflaesk_count}</td>
                                    <td className="py-3 text-right font-semibold">
                                        {p.days_since_last_break ?? "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

const tooltipStyle = {
    backgroundColor: "#fff",
    border: "1px solid #E5E8F0",
    borderRadius: "10px",
    fontFamily: "Manrope",
};

function ChartSection({ title, subtitle, children }) {
    return (
        <div className="bg-white border border-[#E5E8F0] rounded-2xl p-6 md:p-8 mb-8">
            <h2 className="font-serif-dm text-2xl md:text-3xl text-royal">{title}</h2>
            {subtitle && <p className="text-sm text-[#4F5A7A] mt-1 mb-6">{subtitle}</p>}
            {children}
        </div>
    );
}
